import type { Context } from 'hono'
import { ZodError } from 'zod'
import { ForbiddenError as CASLForbiddenError } from '@casl/ability'
import { AppError, ValidationError, ErrorCodes, type ValidationDetail } from '../../core/errors/app-error.js'
import { env } from '@kernel/config/env.js'
import { logger } from '@kernel/logger/index.js'
import type { NexusEnv } from '../types.js'

/** Exception capture function signature (injected to avoid modules/ dependency) */
export type CaptureExceptionFn = (error: Error, context?: Record<string, unknown>) => void

/** Default no-op capture function */
const noopCapture: CaptureExceptionFn = () => {}

/** Injected capture function (set via initErrorMiddleware) */
let captureException: CaptureExceptionFn = noopCapture

/**
 * Initialize error middleware with exception capture function.
 * Called from server bootstrap after logger module is initialized.
 */
export function initErrorMiddleware(options?: { captureException?: CaptureExceptionFn }): void {
  captureException = options?.captureException ?? noopCapture
}

/**
 * Standard API error response with i18n support.
 * Frontend uses 'code' to translate the error message.
 */
interface ApiErrorResponse {
  error: {
    code: string
    message: string
    interpolation?: Record<string, string | number>
    requestId?: string
  }
  details?: ValidationDetail[]
}

/**
 * Creates a standard error response body.
 */
export function createErrorResponse(
  code: string,
  message: string,
  interpolation?: Record<string, string | number>,
  details?: ValidationDetail[],
  requestId?: string
): ApiErrorResponse {
  const response: ApiErrorResponse = {
    error: { code, message }
  }
  if (interpolation) {
    response.error.interpolation = interpolation
  }
  if (requestId) {
    response.error.requestId = requestId
  }
  if (details && details.length > 0) {
    response.details = details
  }
  return response
}

type StatusCode = Parameters<Context['json']>[1]

function getIp(c: Context<NexusEnv>): string | undefined {
  const fwd = c.req.header('x-forwarded-for')
  if (fwd) return fwd.split(',')[0]?.trim()
  return c.req.header('x-real-ip') ?? undefined
}

/**
 * Hono error handler compatible with `app.onError(errorHandler)`.
 * Preserves every branch from the Express error middleware.
 */
export function errorHandler(err: Error, c: Context<NexusEnv>): Response | Promise<Response> {
  const requestId = c.get('requestId')
  const url = c.req.url
  const method = c.req.method
  const ip = getIp(c)

  // Log según tipo (4xx = warn sin stack, 5xx = error con stack)
  if (err instanceof AppError && err.statusCode < 500) {
    logger.warn({ code: err.code, status: err.statusCode, url, method, ip, requestId }, err.message)
  } else if (!(err instanceof ZodError) && !(err instanceof CASLForbiddenError)) {
    logger.error({ err, url, method, requestId }, 'Server error')
  }

  // JSON parse errors (malformed JSON body)
  // Note: Express attached a `body` property to the SyntaxError — Hono does not.
  if (err instanceof SyntaxError) {
    logger.debug({ err, url, method }, 'Invalid JSON body')
    return c.json(
      createErrorResponse(ErrorCodes.VALIDATION_JSON_MALFORMED, `Malformed JSON: ${err.message}`, undefined, undefined, requestId),
      400
    )
  }

  // Payload too large
  if (err.name === 'PayloadTooLargeError' || (err as { type?: string }).type === 'entity.too.large') {
    logger.debug({ err, url, method }, 'Payload too large')
    return c.json(
      createErrorResponse(ErrorCodes.STORAGE_PAYLOAD_TOO_LARGE, 'Payload too large: Request body exceeds the allowed limit (1MB)', undefined, undefined, requestId),
      413
    )
  }

  // Multer file size
  if (err.name === 'MulterError' && (err as { code?: string }).code === 'LIMIT_FILE_SIZE') {
    logger.debug({ err, url, method }, 'File too large')
    return c.json(
      createErrorResponse(ErrorCodes.STORAGE_FILE_TOO_LARGE, 'File too large: Uploaded file exceeds the allowed limit', undefined, undefined, requestId),
      413
    )
  }

  // Other multer errors
  if (err.name === 'MulterError' || err.message?.includes('Invalid filename')) {
    logger.debug({ err, url, method }, 'Upload rejected')
    return c.json(
      createErrorResponse(ErrorCodes.STORAGE_FILE_TYPE_NOT_ALLOWED, `Invalid file: ${err.message}`, undefined, undefined, requestId),
      400
    )
  }

  // Zod validation errors
  if (err instanceof ZodError) {
    const details: ValidationDetail[] = err.errors.map((e) => ({
      path: e.path.join('.'),
      message: e.message,
      code: mapZodCodeToErrorCode(e.code),
      interpolation: extractZodInterpolation(e)
    }))
    return c.json(
      createErrorResponse(ErrorCodes.VALIDATION_ERROR, 'Validation error', undefined, details, requestId),
      400
    )
  }

  // CASL forbidden
  if (err instanceof CASLForbiddenError) {
    return c.json(
      createErrorResponse(ErrorCodes.PERMISSION_DENIED, 'Permission denied', undefined, undefined, requestId),
      403
    )
  }

  // ValidationError (typed details)
  if (err instanceof ValidationError) {
    return c.json(
      createErrorResponse(err.code, err.message, err.interpolation, err.details, requestId),
      400
    )
  }

  // Errors with structured code+statusCode (framework-agnostic services)
  const errCode = (err as { code?: string }).code
  const errStatus = (err as { statusCode?: number }).statusCode
  if (typeof errStatus === 'number' && typeof errCode === 'string' && !(err instanceof AppError)) {
    return c.json(
      createErrorResponse(errCode, err.message, undefined, undefined, requestId),
      errStatus as StatusCode
    )
  }

  // Custom app errors
  if (err instanceof AppError) {
    if (err.statusCode >= 500) {
      captureException(err, { url, method, code: err.code, requestId })
    }

    const status = err.statusCode as StatusCode

    if (typeof err.details === 'string') {
      return c.json(
        createErrorResponse(err.code, `${err.message}: ${err.details}`, err.interpolation, undefined, requestId),
        status
      )
    }

    if (err.details && typeof err.details === 'object') {
      const response = createErrorResponse(err.code, err.message, err.interpolation, undefined, requestId)
      return c.json({ ...response, ...err.details }, status)
    }

    return c.json(
      createErrorResponse(err.code, err.message, err.interpolation, undefined, requestId),
      status
    )
  }

  // DB constraint violations
  const dbCode = (err as { code?: string }).code ?? ''
  const dbErrno = (err as { errno?: number }).errno
  const dbMsg = err.message ?? ''

  if (dbCode === '23505' || dbCode === 'ER_DUP_ENTRY' || dbErrno === 1062 || dbMsg.includes('UNIQUE constraint failed')) {
    return c.json(
      createErrorResponse(ErrorCodes.DB_CONSTRAINT_UNIQUE, 'Unique constraint violation', undefined, undefined, requestId),
      409
    )
  }

  // Unknown
  logger.error({ err, url, method, stack: err.stack, requestId }, 'Unhandled error')
  captureException(err, { url, method, requestId })
  return c.json(
    createErrorResponse(
      ErrorCodes.SYSTEM_INTERNAL_ERROR,
      env.NODE_ENV === 'production' ? 'Internal server error' : err.message,
      undefined,
      undefined,
      requestId
    ),
    500
  )
}

function mapZodCodeToErrorCode(zodCode: string): string {
  switch (zodCode) {
    case 'invalid_type':
    case 'invalid_string':
    case 'invalid_enum_value':
      return 'VALIDATION_FIELD_INVALID'
    case 'too_small':
    case 'too_big':
      return 'VALIDATION_FIELD_INVALID'
    default:
      return 'VALIDATION_FIELD_INVALID'
  }
}

function extractZodInterpolation(error: ZodError['errors'][number]): Record<string, string | number> | undefined {
  const interpolation: Record<string, string | number> = {}
  if ('minimum' in error) {
    interpolation['min'] = error.minimum as number
  }
  if ('maximum' in error) {
    interpolation['max'] = error.maximum as number
  }
  return Object.keys(interpolation).length > 0 ? interpolation : undefined
}
